import {
	Map as ImmutableMap,
	OrderedMap as ImmutableOrderedMap,
	Set as ImmutableSet,
	type OrderedMap,
} from "immutable";

import { type Operation, applyPatch } from "../../fast-json-patch/core";
import { Tuple } from "../Tuple";
import { type Primitive, isPrimitive } from "../types";
import { type Change } from "./Change";

export type TuplesMutation<TOut> =
	| {
			type: "INSERT";
			recordId: string;
			record: Tuple<TOut>;
	  }
	| {
			type: "UPDATE";
			recordId: string;
			change: Operation;
	  }
	| {
			type: "DELETE";
			recordId: string;
	  };

export const insert = <T>(record: Tuple<T>) =>
	({ type: "INSERT", recordId: record.id, record }) satisfies TuplesMutation<T>;

export const update = <T>(recordId: string, change: Operation) =>
	({ type: "UPDATE", recordId, change }) satisfies TuplesMutation<T>;

export const deleteChange = <T>(recordId: string) =>
	({ type: "DELETE", recordId }) satisfies TuplesMutation<T>;

const tuplesMutationToChange = <T>(tuples: Tuples<T>, change: TuplesMutation<T>): Change<T> => {
	switch (change.type) {
		case "INSERT":
			return { type: "INSERT", recordId: change.recordId, record: change.record };
		case "UPDATE": {
			const output = tuples.get(change.recordId);
			if (!output) {
				throw new Error(`Record ${change.recordId} not found`);
			}
			return { type: "UPDATE", recordId: change.recordId, change: change.change, output };
		}
		case "DELETE":
			return { type: "DELETE", recordId: change.recordId };
	}
};

export function mutateTuples<T>(
	tuples: Tuples<T>,
	changes: TuplesMutation<T>[]
): { data: Tuples<T>; changes: Change<T>[] } {
	const updatedTable = tuples.update(changes);
	const tableChanges = changes.map((change) => tuplesMutationToChange(tuples, change));
	return { data: updatedTable, changes: tableChanges };
}

export class Tuples<T> {
	private readonly _map: OrderedMap<string, Tuple<T>>;
	private readonly _hashIndex: ImmutableMap<number, ImmutableSet<string>>;

	private constructor(
		map: OrderedMap<string, Tuple<T>>,
		hashIndex: ImmutableMap<number, ImmutableSet<string>>
	) {
		this._map = map;
		this._hashIndex = hashIndex;
	}

	static empty<T>(): Tuples<T> {
		return new Tuples(ImmutableOrderedMap(), ImmutableMap());
	}

	static from<T>(...map: Tuple<T>[]): Tuples<T> {
		return new Tuples(
			ImmutableOrderedMap(map.map((tuple) => [tuple.id, tuple])),
			map.reduce((acc, tuple) => {
				const hash = tuple.getHashCode();
				const hashSet = acc.get(hash, ImmutableSet<string>());
				return acc.set(hash, hashSet.add(tuple.id));
			}, ImmutableMap<number, ImmutableSet<string>>())
		);
	}

	hasId(id: string): boolean {
		return this._map.has(id);
	}

	/**
	 * Check if any tuple in the set equals the target according to SQLite rules
	 */
	hasValue<TCmp>(target: Tuple<TCmp>): boolean {
		const hash = target.getHashCode();
		const candidateIds = this._hashIndex.get(hash);
		if (!candidateIds) {
			return false;
		}

		// Check each candidate for exact equality
		for (const id of candidateIds) {
			if (target.compare("=", this._map.get(id)!)) {
				return true;
			}
		}
		return false;
	}

	get(id: string): Tuple<T> | undefined {
		return this._map.get(id);
	}

	set(id: string, value: Tuple<T>): Tuples<T> {
		const hash = value.getHashCode();
		let newHashIndex = this._hashIndex;

		// Update hash index
		const hashSet = this._hashIndex.get(hash, ImmutableSet<string>());
		newHashIndex = newHashIndex.set(hash, hashSet.add(id));

		return new Tuples(this._map.set(id, value), newHashIndex);
	}

	delete(id: string): Tuples<T> {
		const value = this._map.get(id);
		if (!value) {
			return this;
		}

		const hash = value.getHashCode();
		let newHashIndex = this._hashIndex;
		const hashSet = this._hashIndex.get(hash, ImmutableSet<string>()).remove(id);
		if (hashSet.size > 0) {
			newHashIndex = newHashIndex.set(hash, hashSet);
		} else {
			newHashIndex = newHashIndex.delete(hash);
		}

		return new Tuples(this._map.delete(id), newHashIndex);
	}

	toOrderedMap(): OrderedMap<string, Tuple<T>> {
		return this._map;
	}

	resolveToTuple(): Tuple<T> {
		if (this._map.size !== 1) {
			throw new Error("Tuples must have exactly one record to resolve to a tuple");
		}

		const tuple = this._map.first();
		if (!tuple) {
			throw new Error("Tuples must have exactly one record to resolve to a scalar");
		}
		return tuple;
	}

	/**
	 * Resolves a Tuples to a single scalar value, following SQLite rules:
	 * - Must have exactly one record
	 * - Record must have exactly one value
	 * - Value must be a primitive type
	 *
	 * @returns The scalar value, or null if any condition is not met
	 */
	resolveToScalar(): Primitive {
		const record = this.resolveToTuple();
		const value = record.getValue();

		// For primitive values, return directly
		if (isPrimitive(value)) {
			return value;
		}

		// For objects, must have exactly one key with a primitive value
		if (typeof value === "object" && value !== null) {
			const entries = Object.entries(value);
			if (entries.length !== 1) {
				throw new Error("Tuples must have exactly one record to resolve to a scalar");
			}

			const [, firstValue] = entries[0]!;
			if (!isPrimitive(firstValue)) {
				throw new Error("Tuples must have exactly one record to resolve to a scalar");
			}
			return firstValue;
		}

		throw new Error("Tuples must have exactly one record to resolve to a scalar");
	}

	/**
	 * Converts the Tuples to a plain JavaScript object where:
	 * - Keys are the record IDs
	 * - Values are the unwrapped tuple values
	 */
	toObject(): Record<string, T> {
		const o: Record<string, T> = {};
		for (const [id, tuple] of this._map.entries()) {
			o[id] = tuple.getValue();
		}
		return o;
	}

	toArray(): [string, T][] {
		return this._map.map((tuple) => tuple.getValue()).toArray();
	}

	get size(): number {
		return this._map.size;
	}

	/**
	 * Returns a new Tuples with records sorted according to the compare function
	 * @param compareFn Function that defines the sort order
	 */
	sort(compareFn: (a: Tuple<T>, b: Tuple<T>) => number): Tuples<T> {
		return new Tuples(
			this._map.sort((a, b) => compareFn(a, b)),
			this._hashIndex
		);
	}

	/**
	 * Returns a new Tuples skipping the first n records
	 */
	skip(n: number): Tuples<T> {
		const skipped = this._map.take(n);

		let curr = new Tuples(this._map, this._hashIndex);
		for (const [id] of skipped) {
			curr = curr.delete(id);
		}

		return curr;
	}

	/**
	 * Returns a new Tuples with at most n records
	 */
	take(n: number): Tuples<T> {
		const newMap = this._map.take(n);

		let curr = new Tuples(ImmutableOrderedMap<string, Tuple<T>>(), ImmutableMap());
		for (const [id] of newMap) {
			curr = curr.set(id, this._map.get(id)!);
		}
		return curr;
	}

	/**
	 * Returns a new Tuples with only records that match the predicate
	 */
	filter(predicate: (value: Tuple<T>, id: string) => boolean): Tuples<T> {
		let result = Tuples.empty<T>();
		for (const [id, value] of this._map) {
			if (predicate(value, id)) {
				result = result.set(id, value);
			}
		}
		return result;
	}

	/**
	 * Returns a new Tuples with transformed values
	 */
	map<R>(transform: (value: Tuple<T>, id: string) => Tuple<R>): Tuples<R> {
		let result = Tuples.empty<R>();
		for (const [id, value] of this._map) {
			result = result.set(id, transform(value, id));
		}
		return result;
	}

	update<TUpdated>(changes: TuplesMutation<TUpdated>[]): Tuples<TUpdated> {
		let result = new Tuples<TUpdated>(
			this._map as unknown as OrderedMap<string, Tuple<TUpdated>>,
			this._hashIndex
		);

		for (const change of changes) {
			switch (change.type) {
				case "INSERT":
					result = result.set(change.recordId, change.record as Tuple<TUpdated>);
					break;

				case "UPDATE": {
					const current = result.get(change.recordId);
					if (!current) {
						break;
					}

					const updated = applyPatch(
						current.getValue(),
						[change.change],
						true,
						false,
						true
					).newDocument;
					result = result.set(
						change.recordId,
						Tuple.from(change.recordId, current.getOwner(), updated)
					);
					break;
				}

				case "DELETE":
					result = result.delete(change.recordId);
					break;
			}
		}
		return result;
	}
}
